import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Page from "../../../components/Page";
import dashboardService from "./services/dashboardService";

const NAVY = "#004A78";
const LINE_COLORS = ["#004A78", "#2E7D32", "#F9A825", "#6A1B9A", "#C62828", "#00838F", "#EF6C00", "#5D4037"];
const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];
const YEARS = Array.from({ length: new Date().getFullYear() - 2019 }, (_, index) => 2020 + index);
const MAX_SELECTED = 8;

const money = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

const customerLabel = (row: any) =>
  row.initials ? `${row.initials} — ${row.name}` : row.name;
const shortName = (row: any) => row.initials || String(row.name || "").slice(0, 14);

const ChartCard = ({ title, children }: { title: string; children: any }) => (
  <Card sx={{ p: 2, height: 300, display: "flex", flexDirection: "column" }}>
    <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
  </Card>
);

const MonthSelect = ({
  label,
  month,
  year,
  onMonth,
  onYear,
}: {
  label: string;
  month: number;
  year: number;
  onMonth: (value: number) => void;
  onYear: (value: number) => void;
}) => (
  <Stack direction="row" spacing={1}>
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel>{label}</InputLabel>
      <Select value={month} label={label} onChange={(event) => onMonth(Number(event.target.value))}>
        {MONTHS.map((item) => (
          <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 90 }}>
      <InputLabel>Ano</InputLabel>
      <Select value={year} label="Ano" onChange={(event) => onYear(Number(event.target.value))}>
        {YEARS.map((item) => (
          <MenuItem key={item} value={item}>{item}</MenuItem>
        ))}
      </Select>
    </FormControl>
  </Stack>
);

export default function CustomerCompare() {
  const now = new Date();
  const [mode, setMode] = useState<"month" | "range">("month");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [fromMonth, setFromMonth] = useState(1);
  const [fromYear, setFromYear] = useState(2020);
  const [toMonth, setToMonth] = useState(now.getMonth() + 1);
  const [toYear, setToYear] = useState(now.getFullYear());
  const [boundsReady, setBoundsReady] = useState(false);
  const [branch, setBranch] = useState("Todas");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({});
  const [period, setPeriod] = useState("");
  const [selected, setSelected] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);
  const [rangeExtras, setRangeExtras] = useState<any>(null);

  const queryParams = () =>
    mode === "range"
      ? {
          mode: "range" as const,
          fromMonth: boundsReady ? fromMonth : undefined,
          fromYear: boundsReady ? fromYear : undefined,
          toMonth: boundsReady ? toMonth : undefined,
          toYear: boundsReady ? toYear : undefined,
          branch,
        }
      : { mode: "month" as const, month, year, branch };

  const load = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getCustomerComparison(queryParams());
      const nextRows = response.data.rows || [];
      setRows(nextRows);
      setSummary(response.data.summary || {});
      setPeriod(response.data.period?.label || "");
      if (response.data.mode === "range" && response.data.period) {
        setFromMonth(response.data.period.fromMonth);
        setFromYear(response.data.period.fromYear);
        setToMonth(response.data.period.toMonth);
        setToYear(response.data.period.toYear);
        setBoundsReady(true);
        setRangeExtras({
          topCustomers: response.data.topCustomers || [],
          billedOverTime: response.data.billedOverTime || [],
          pairsOverTime: response.data.pairsOverTime || [],
          marketTotals: response.data.marketTotals || [],
        });
      } else if (response.data.period?.month) {
        setMonth(response.data.period.month);
        setYear(response.data.period.year);
        setRangeExtras(null);
      }
      setSelected((current) => {
        if (!current.length) return nextRows.slice(0, 6);
        const stillThere = current
          .map((item) => nextRows.find((row: any) => row.customerId === item.customerId))
          .filter(Boolean);
        return stillThere.length ? stillThere : nextRows.slice(0, 6);
      });
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    const loadDetail = async () => {
      if (!selected.length) {
        setDetail(null);
        return;
      }
      try {
        const response = await dashboardService.getCustomerCompareDetail({
          ...queryParams(),
          ids: selected.map((item) => item.customerId).join(","),
        });
        setDetail(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.map((item) => item.customerId).join(","), mode, period]);

  const chartRows = useMemo(
    () =>
      selected.map((row) => ({
        ...row,
        label: shortName(row),
        premiumPct: Number(row.premiumShare || 0) * 100,
        billedDeltaPct: Number(row.billedDelta || 0) * 100,
        pairsDeltaPct: Number(row.pairsDelta || 0) * 100,
        discountPct: Number(row.discountShare || 0) * 100,
      })),
    [selected],
  );

  const mixChart = useMemo(
    () =>
      (detail?.mix || []).map((item: any) => ({
        name: selected.find((row) => row.customerId === item.customerId)?.initials || item.name,
        premium: item.mix
          .filter((product: any) => product.premium)
          .reduce((sum: number, product: any) => sum + product.pairs, 0),
        basic: item.mix
          .filter((product: any) => !product.premium)
          .reduce((sum: number, product: any) => sum + product.pairs, 0),
      })),
    [detail, selected],
  );

  const selectedLines = (detail?.customers || []).map((customer: any, index: number) => ({
    ...customer,
    color: LINE_COLORS[index % LINE_COLORS.length],
    label: selected.find((row) => row.customerId === customer.customerId)?.initials || customer.name,
  }));

  return (
    <Page title="Comparativo de clientes | Optools">
      <Stack spacing={1.25} sx={{ mb: 1.5 }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.25}
          alignItems={{ lg: "center" }}
          sx={{ flexWrap: "wrap" }}
        >
          <Typography variant="h6" sx={{ color: NAVY, fontWeight: 700 }}>
            Comparativo de clientes
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_event, value) => value && setMode(value)}
          >
            <ToggleButton value="month">Mês específico</ToggleButton>
            <ToggleButton value="range">Período</ToggleButton>
          </ToggleButtonGroup>
          {mode === "month" ? (
            <MonthSelect label="Mês" month={month} year={year} onMonth={setMonth} onYear={setYear} />
          ) : (
            <>
              <MonthSelect label="De" month={fromMonth} year={fromYear} onMonth={setFromMonth} onYear={setFromYear} />
              <MonthSelect label="Até" month={toMonth} year={toYear} onMonth={setToMonth} onYear={setToYear} />
            </>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Unidade</InputLabel>
            <Select value={branch} label="Unidade" onChange={(event) => setBranch(event.target.value)}>
              {["Todas", "NE", "SP", "PA"].map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="contained" onClick={load} sx={{ height: 36, bgcolor: NAVY }}>
            {loading ? <CircularProgress size={18} color="inherit" /> : "Atualizar"}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {period} · {summary.customers || 0} clientes · {money(summary.billed || 0)}
          </Typography>
        </Stack>

        <Autocomplete
          multiple
          options={rows}
          value={selected}
          disableCloseOnSelect
          filterSelectedOptions
          getOptionLabel={customerLabel}
          isOptionEqualToValue={(option, value) => option.customerId === value.customerId}
          onChange={(_event, value) => setSelected(value.slice(0, MAX_SELECTED))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.customerId} size="small" label={customerLabel(option)} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={`Clientes no gráfico (até ${MAX_SELECTED})`}
              placeholder={selected.length ? "" : "Busque e selecione os clientes"}
            />
          )}
        />
      </Stack>

      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box>
      ) : !chartRows.length ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography>Nenhum cliente no período. Ajuste o filtro e atualize.</Typography>
        </Card>
      ) : mode === "month" ? (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Faturado no mês">
              <ResponsiveContainer>
                <BarChart data={chartRows} layout="vertical" margin={{ left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <YAxis type="category" dataKey="label" width={70} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => money(Number(value))} />
                  <Bar dataKey="billed" name="Faturado" fill="#004A78" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Volume (pares)">
              <ResponsiveContainer>
                <BarChart data={chartRows} layout="vertical" margin={{ left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="label" width={70} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => `${num(Number(value))} pares`} />
                  <Bar dataKey="pairs" name="Pares" fill="#2E7D32" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard title="Ticket médio">
              <ResponsiveContainer>
                <BarChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => money(Number(value))} />
                  <Bar dataKey="averageTicket" name="Ticket" fill="#F9A825" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard title="% premium">
              <ResponsiveContainer>
                <BarChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="premiumPct" name="Premium" fill="#6A1B9A" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard title="Mix AR no mês">
              <ResponsiveContainer>
                <BarChart data={mixChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <Legend />
                  <Tooltip />
                  <Bar dataKey="basic" name="Class + Extra Clean" stackId="a" fill="#90A4AE" />
                  <Bar dataKey="premium" name="Premium" stackId="a" fill="#004A78" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Variação de faturado vs mês anterior">
              <ResponsiveContainer>
                <BarChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="billedDeltaPct" name="Δ Faturado" fill="#00838F" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Variação de pares vs mês anterior">
              <ResponsiveContainer>
                <BarChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="pairsDeltaPct" name="Δ Pares" fill="#2E7D32" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12}>
            <ChartCard title="Faturamento 12 meses">
              <ResponsiveContainer>
                <LineChart data={detail?.monthlySeries || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <Tooltip formatter={(value: any) => money(Number(value))} />
                  <Legend />
                  {selectedLines.map((customer: any) => (
                    <Line key={customer.customerId} type="monotone" dataKey={String(customer.customerId)} name={customer.label} stroke={customer.color} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Faturamento ao longo do tempo">
              <ResponsiveContainer>
                <LineChart data={detail?.monthlySeries || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <Tooltip formatter={(value: any) => money(Number(value))} />
                  <Legend />
                  {selectedLines.map((customer: any) => (
                    <Line key={customer.customerId} type="monotone" dataKey={String(customer.customerId)} name={customer.label} stroke={customer.color} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Pares ao longo do tempo">
              <ResponsiveContainer>
                <LineChart data={detail?.pairsSeries || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => `${num(Number(value))} pares`} />
                  <Legend />
                  {selectedLines.map((customer: any) => (
                    <Line key={customer.customerId} type="monotone" dataKey={String(customer.customerId)} name={customer.label} stroke={customer.color} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Variação de faturamento (primeiro vs último mês)">
              <ResponsiveContainer>
                <BarChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="billedDeltaPct" name="Δ Faturado" fill="#00838F" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Variação de pares (primeiro vs último mês)">
              <ResponsiveContainer>
                <BarChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value: any) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="pairsDeltaPct" name="Δ Pares" fill="#2E7D32" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12}>
            <ChartCard title="Maiores clientes ao longo do tempo">
              <ResponsiveContainer>
                <AreaChart data={rangeExtras?.billedOverTime || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <Tooltip formatter={(value: any) => money(Number(value))} />
                  <Legend />
                  {(rangeExtras?.topCustomers || []).map((customer: any, index: number) => (
                    <Area
                      key={customer.customerId}
                      type="monotone"
                      dataKey={String(customer.customerId)}
                      name={customer.name}
                      stackId="1"
                      stroke={LINE_COLORS[index % LINE_COLORS.length]}
                      fill={LINE_COLORS[index % LINE_COLORS.length]}
                      fillOpacity={0.35}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Mercado no período (faturado x pares)">
              <ResponsiveContainer>
                <BarChart data={rangeExtras?.marketTotals || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tickFormatter={(value) => `${Math.round(Number(value) / 1000)}k`} />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="billed" name="Faturado" fill="#004A78" />
                  <Bar yAxisId="right" dataKey="pairs" name="Pares" fill="#F9A825" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Ticket e premium no período">
              <ResponsiveContainer>
                <BarChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="averageTicket" name="Ticket médio" fill="#F9A825" />
                  <Bar dataKey="premiumPct" name="% premium" fill="#6A1B9A" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Page>
  );
}
